import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, NavigationStart, Router } from '@angular/router';
import { Observable, of, timer } from 'rxjs';
import { catchError, map, switchMap, takeUntil } from 'rxjs/operators';
import { SharedDataService } from 'src/app/service/shared-service/shared-data.service';
import { MenuService } from '../menu-service/menu.service';
import { componentDestroyed } from '@w11k/ngx-componentdestroyed';
import { environment } from 'src/environments/environment';
import { TranslationService } from 'src/app/service/translation-service/translation.service';
import { StagingService } from 'src/app/component/assigned-trips/staging/staging.service';

@Component({
  selector: 'app-side-nav',
  templateUrl: './side-nav.component.html',
  styleUrls: ['./side-nav.component.css']
})
export class SideNavComponent implements OnInit, OnDestroy {
  user;
  menus;
  selectedMenu;
  // isSubMenu = false;
  isShowSmallScreenMenu = false;
  selectedMenukey;
  isRouterEventsExist = false;
  isDev = environment.production ? false : true;
  devUrl = environment.baseUrl;

  constructor(
    private sharedDataService: SharedDataService,
    private menuService: MenuService,
    public sanitizer: DomSanitizer,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private translationService: TranslationService,
    private stagingService: StagingService
  ) { }

  ngOnInit() {
    this.router.events
    .pipe(takeUntil(componentDestroyed(this)))
    .subscribe((event) => {
      this.isRouterEventsExist = true;
      if (event instanceof NavigationEnd) {
        if (event.url) {
          this.selectMenuDynamically(event.url);
        }
      }
    });
    this.getUser();
    this.getSubMenus();
    this.getSmallScreenMenuState();
    this.getUrlOnPageReload();
    this.startIntakeCountPolling();

    this.translationService.locale$
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(() => {
        this.loadMenus();
      });
  }

  getUrlOnPageReload() {
    if (this.isRouterEventsExist) return;
    if(this.router.url) {
      this.selectMenuDynamically(this.router.url);
    }
  }

  selectMenuDynamically(routeUrl) {
    if (!this.selectedMenu) {
      const url = routeUrl.split("/");
      if (url[1] === "dashboard" || url[1] === "") {
        this.selectedMenu = "dashboard";
      } else {
        this.selectedMenu = url[1];
      }
      this.onSelectMenu(this.menus ? this.menus[this.selectedMenu] : null);
    }
  }

  getUser() {
    this.sharedDataService
      .getUser()
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(user => {
        if (user) {
          this.user = user;
          this.loadMenus();
        }
      });
  }

  private loadMenus() {
    if (!this.user || !this.user.roles) {
      return;
    }
    if ((typeof this.user.roles) === 'string') {
      if (this.user.sRoles) {
        this.menus = this.menuService.getMenu(this.user.sRoles, this.user);
      } else {
        this.menus = this.menuService.getMenu(this.user.roles, this.user);
      }
    } else if ((typeof this.user.roles) === 'object') {
      this.menus = this.menuService.getMenu(this.user.roles[0], this.user);
    }

    if (this.menus) {
      Object.keys(this.menus).forEach(key => {
        this.menus[key].sanitizedIcon = this.sanitizer.bypassSecurityTrustHtml(this.menus[key].icon);
      });

      // Keep header submenu titles in sync after language change
      if (this.selectedMenu && typeof this.selectedMenu === 'object' && this.selectedMenu.key) {
        const updated = this.menus[this.selectedMenu.key];
        if (updated) {
          this.onSelectMenu(updated);
        }
      }
    }
  }

  sortMethod(a, b) {
    return a.value.position > b.value.position ? 1 : -1;
  }

  onSelectMenu(menu) {
    this.selectedMenu = menu;
    this.sharedDataService.saveSubMenus(menu ? menu : null);
  }

  getSubMenus() {
    this.sharedDataService
      .getSubMenus()
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(menu => {
        if (menu && this.selectedMenu) {
          if (this.selectedMenu.key !== menu.key) {
            this.selectedMenu = menu;
          }
        }
      });
  }

  onSelectMenuSmallScreen(menu) {
    this.selectedMenu = menu;
    this.sharedDataService.saveSubMenus(menu ? menu : null);
    if (this.selectedMenukey === menu.key) {
      this.selectedMenukey = '';
    } else {
      this.selectedMenukey = menu.key;
    }
    this.sharedDataService.setSmallScreenMenuState(false);
  }

  getSmallScreenMenuState() {
    this.sharedDataService
      .getSmallScreenMenuState()
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(data => {
        this.isShowSmallScreenMenu = data;
      });
  }

  closeSmallScreenMenu() {
    this.sharedDataService.setSmallScreenMenuState(false);
  }

  setMenuCss(evt) {
    return 'bg-nav-light text-nav-light';
  }

  @HostListener('window:popstate', ['$event'])
  onPopState(event) {
    if (location.href) {
      let currentUrl = location.href.replace('http://','');
      currentUrl = currentUrl.replace('https://','');
      const url = currentUrl.split("/");
      if (url[1] === "dashboard" || url[1] === "") {
        this.selectedMenu = "dashboard";
      } else {
        this.selectedMenu = url[1];
      }
      this.onSelectMenu(this.menus ? this.menus[this.selectedMenu] : null);
    }
  }

  private startIntakeCountPolling(): void {
    timer(0, 20000)
      .pipe(
        takeUntil(componentDestroyed(this)),
        switchMap(() => {
          return this.stagingService.getIntakeCount().pipe(
            catchError(() => of(null))
          );
        })
      )
      .subscribe((res) => {
        if (res !== null && res !== undefined) {
          const count = this.extractIntakeCount(res);
          this.updateStagingMenuCount(count);
        }
      });
  }

  private extractIntakeCount(res: any): number | null {
    if (res === null || res === undefined) {
      return null;
    }
    if (typeof res === 'number') {
      return res;
    }
    if (typeof res === 'string') {
      const parsed = parseInt(res, 10);
      return isNaN(parsed) ? null : parsed;
    }
    if (typeof res === 'object') {
      if (typeof res.count === 'number') {
        return res.count;
      }
      if (typeof res.intakeCount === 'number') {
        return res.intakeCount;
      }
      if (typeof res.total === 'number') {
        return res.total;
      }
      if (typeof res.totalCount === 'number') {
        return res.totalCount;
      }
      if (res.data !== undefined && res.data !== null) {
        if (typeof res.data === 'number') {
          return res.data;
        }
        if (typeof res.data === 'string') {
          const p = parseInt(res.data, 10);
          return isNaN(p) ? null : p;
        }
        if (typeof res.data === 'object') {
          if (typeof res.data.count === 'number') {
            return res.data.count;
          }
          if (typeof res.data.intakeCount === 'number') {
            return res.data.intakeCount;
          }
          if (typeof res.data.total === 'number') {
            return res.data.total;
          }
        }
      }
      if (res.result && typeof res.result.count === 'number') {
        return res.result.count;
      }
      if (res.payload && typeof res.payload.count === 'number') {
        return res.payload.count;
      }
    }
    return null;
  }

  private updateStagingMenuCount(count: number | null): void {
    if (this.menus && this.menus['staging']) {
      this.menus['staging'].count = count;
    }
    this.menuService.updateMenuCount('staging', count);
  }

  ngOnDestroy(): void {
  }
}
